import type React from 'react'
import { type FeatureFlagKey, useFeatureFlags } from '../../hooks/useFeatureFlags'

interface FeatureFlagProps {
  flag: FeatureFlagKey
  children: React.ReactNode
  fallback?: React.ReactNode
  loading?: React.ReactNode
}

/**
 * FeatureFlag component for conditionally rendering content based on feature flags
 *
 * @param flag - The feature flag key to check
 * @param children - Content to render when the feature is enabled
 * @param fallback - Optional content to render when the feature is disabled
 * @param loading - Optional content to render while loading feature flags
 */
export const FeatureFlag: React.FC<FeatureFlagProps> = ({ flag, children, fallback = null, loading = null }) => {
  const { isFeatureEnabled, isLoading: featuresLoading } = useFeatureFlags()

  if (featuresLoading && loading) {
    return <>{loading}</>
  }

  if (isFeatureEnabled(flag)) {
    return <>{children}</>
  }

  return <>{fallback}</>
}

interface MultiFeatureFlagProps {
  flags: FeatureFlagKey[]
  mode?: 'all' | 'any' // 'all' requires all flags to be enabled, 'any' requires at least one
  children: React.ReactNode
  fallback?: React.ReactNode
  loading?: React.ReactNode
}

/**
 * MultiFeatureFlag component for conditionally rendering content based on multiple feature flags
 */
export const MultiFeatureFlag: React.FC<MultiFeatureFlagProps> = ({
  flags,
  mode = 'all',
  children,
  fallback = null,
  loading = null,
}) => {
  const { isFeatureEnabled, isLoading: featuresLoading } = useFeatureFlags()

  if (featuresLoading && loading) {
    return <>{loading}</>
  }

  const hasRequiredFlags =
    mode === 'all' ? flags.every((flag) => isFeatureEnabled(flag)) : flags.some((flag) => isFeatureEnabled(flag))

  if (hasRequiredFlags) {
    return <>{children}</>
  }

  return <>{fallback}</>
}
